import {
  Component,
  DestroyRef,
  ElementRef,
  ViewEncapsulation,
  afterNextRender,
  inject,
  viewChild,
} from '@angular/core';

/**
 * Pont de transition : monte un composant de l'ancien front dans le nouveau shell.
 *
 * styles.scss est intégralement encapsulé dans `.old-theme`, or le nouveau shell porte
 * `.new-theme` : un composant legacy rendu tel quel dans /new-site perd donc la totalité
 * de ses styles globaux. Ce wrapper restaure ce contexte pour son contenu projeté.
 *
 * L'ancien front dimensionnait ses panneaux sur le viewport (`height: 100vh`,
 * `max-height: calc(100vh - 12rem)`, `width: 120vh`). legacy-host.component.scss remplace
 * ces valeurs par la hauteur réellement disponible, exposée en `--legacy-host-height`.
 * Toutes ses règles sont préfixées `.old-theme.legacy-host` : elles ne s'appliquent jamais
 * à l'ancien front. C'est le seul endroit où ajuster la mise en page d'un onglet legacy.
 *
 * À supprimer quand le dernier onglet aura été refondu.
 */
@Component({
  selector:      'app-legacy-host',
  standalone:    true,
  template:      `<div #container class="old-theme legacy-host"><ng-content /></div>`,
  styleUrls:     ['./legacy-host.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class LegacyHostComponent {
  private readonly container = viewChild.required<ElementRef<HTMLElement>>('container');

  constructor() {
    const observer = new ResizeObserver(entries => this.exposeAvailableHeight(entries));
    afterNextRender(() => observer.observe(this.container().nativeElement));
    inject(DestroyRef).onDestroy(() => observer.disconnect());
  }

  /**
   * Publie la hauteur utile de la zone (hors padding) en variable CSS. Un `height: 100%`
   * ne traverse pas les éléments intermédiaires sans hauteur des composants legacy ;
   * une custom property, elle, est héritée jusqu'au panneau qui en a besoin.
   */
  private exposeAvailableHeight(entries: ResizeObserverEntry[]): void {
    for (const entry of entries) {
      (entry.target as HTMLElement).style.setProperty('--legacy-host-height', `${entry.contentRect.height}px`);
    }
  }
}
